import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import PosterCarousel from "@/components/feature/PosterCarousel";
import VideoStage from "@/pages/player/components/VideoStage";
import EpisodePanel from "@/pages/player/components/EpisodePanel";
import { dramas } from "@/mocks/dramas";
import { useSeo } from "@/hooks/useSeo";
import { useWatchHistory } from "@/hooks/useWatchHistory";
import { categoryLabel, getDramaById, regionLabel, relatedDramas } from "@/utils/drama";

const DEMO_VIDEO = "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4";
const FREE_COUNT = 18;

export default function Player() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addEntry } = useWatchHistory();
  const [favorited, setFavorited] = useState(false);
  const [copied, setCopied] = useState(false);

  const found = getDramaById(id, dramas);
  const drama = found ?? dramas[0];
  const maxEpisode = Math.min(drama.episodes, 100);
  const requested = Number(searchParams.get("ep")) || 1;
  const episode = Math.min(Math.max(requested, 1), maxEpisode);
  const recommendations = relatedDramas(drama, dramas, 10);
  const pageUrl = `https://www.juyan-drama.com/drama/${drama.id}`;

  useEffect(() => {
    addEntry(drama.id, episode);
  }, [addEntry, drama.id, episode]);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2200);
    return () => window.clearTimeout(timer);
  }, [copied]);

  useSeo({
    title: `${drama.title} 第${episode}集 - ${drama.status}${drama.episodes}集高清在线观看 | 剧焰短剧`,
    description: `《${drama.title}》${drama.year}年${regionLabel(drama.region)}${categoryLabel(
      drama.category
    )}微短剧，${drama.status}共${drama.episodes}集。${drama.synopsis.slice(0, 62)}…剧焰短剧提供高清在线观看与全集选集。`,
    keywords: `${drama.title},${drama.title}全集,${drama.title}第${episode}集,${categoryLabel(drama.category)},微短剧在线观看`,
    image: drama.cover,
    type: "video.other",
    path: `/drama/${drama.id}`,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "VideoObject",
          name: `${drama.title} 第${episode}集`,
          description: drama.synopsis,
          thumbnailUrl: [drama.cover],
          uploadDate: `${drama.publishedAt}T09:00:00+08:00`,
          duration: "PT3M",
          inLanguage: "zh-CN",
          genre: categoryLabel(drama.category),
          isFamilyFriendly: true,
          embedUrl: pageUrl,
          potentialAction: {
            "@type": "WatchAction",
            target: pageUrl,
          },
        },
        {
          "@type": "Movie",
          name: drama.title,
          alternateName: `${drama.title}（短剧）`,
          image: drama.cover,
          description: drama.synopsis,
          datePublished: `${drama.year}-01-01`,
          genre: [categoryLabel(drama.category), "微短剧", "短剧"],
          inLanguage: "zh-CN",
          duration: "PT3M",
          director: { "@type": "Person", name: drama.director },
          actor: drama.cast.map((name) => ({ "@type": "Person", name })),
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: drama.rating,
            bestRating: 10,
            worstRating: 1,
            ratingCount: Math.round(drama.viewsNumber / 12000),
          },
          url: pageUrl,
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "首页",
              item: "https://www.juyan-drama.com/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: categoryLabel(drama.category),
              item: `https://www.juyan-drama.com/category?type=${drama.category}`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: drama.title,
              item: pageUrl,
            },
          ],
        },
      ],
    },
  });

  const goEpisode = (next: number) => {
    const target = Math.min(Math.max(next, 1), maxEpisode);
    const params = new URLSearchParams(searchParams);
    params.set("ep", String(target));
    setSearchParams(params, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const share = () => {
    const link = `${window.location.origin}/drama/${drama.id}?ep=${episode}`;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      void navigator.clipboard.writeText(link).then(() => setCopied(true)).catch(() => setCopied(false));
    }
  };

  if (!found) {
    return (
      <>
        <SiteHeader />
        <main className="flex min-h-[70vh] w-full flex-col items-center justify-center px-4 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-foreground-50/[0.06] text-foreground-500">
            <i className="ri-error-warning-line text-3xl" aria-hidden="true" />
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-50">剧集不存在或已下架</h1>
          <p className="mt-2 text-sm text-foreground-500">你访问的短剧可能已经更名，去分类页看看其他热门作品吧。</p>
          <Link
            to="/category"
            className="mt-6 cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-bold text-background-950 transition-colors hover:bg-primary-400"
          >
            浏览全部短剧
          </Link>
        </main>
        <SiteFooter />
      </>
    );
  }

  return (
    <>
      <SiteHeader />
      <main className="w-full px-4 md:px-6">
        {/* 面包屑 */}
        <nav
          aria-label="面包屑导航"
          className="flex flex-wrap items-center gap-1.5 pt-5 text-xs text-foreground-500 md:text-sm"
        >
          <Link to="/" className="cursor-pointer transition-colors hover:text-primary-300">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-sm text-foreground-700" aria-hidden="true" />
          <Link
            to={`/category?type=${drama.category}`}
            className="cursor-pointer transition-colors hover:text-primary-300"
          >
            {categoryLabel(drama.category)}
          </Link>
          <i className="ri-arrow-right-s-line text-sm text-foreground-700" aria-hidden="true" />
          <span className="text-foreground-200">{drama.title}</span>
        </nav>

        <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
          <div className="min-w-0">
            <VideoStage
              key={`${drama.id}-${episode}`}
              src={DEMO_VIDEO}
              poster={drama.cover}
              title={drama.title}
              episode={episode}
              total={drama.episodes}
            />

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => goEpisode(episode - 1)}
                disabled={episode <= 1}
                className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border border-foreground-50/10 px-3 py-1.5 text-xs text-foreground-300 transition-colors hover:border-primary-400/60 hover:text-primary-300 disabled:cursor-not-allowed disabled:opacity-40 md:text-sm"
              >
                <i className="ri-skip-back-line text-sm" aria-hidden="true" />
                上一集
              </button>
              <button
                type="button"
                onClick={() => goEpisode(episode + 1)}
                disabled={episode >= maxEpisode}
                className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border border-foreground-50/10 px-3 py-1.5 text-xs text-foreground-300 transition-colors hover:border-primary-400/60 hover:text-primary-300 disabled:cursor-not-allowed disabled:opacity-40 md:text-sm"
              >
                下一集
                <i className="ri-skip-forward-line text-sm" aria-hidden="true" />
              </button>

              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setFavorited((prev) => !prev)}
                  className={`flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs transition-colors md:text-sm ${
                    favorited
                      ? "border-accent-500/60 bg-accent-500/15 text-accent-300"
                      : "border-foreground-50/10 text-foreground-300 hover:border-accent-400/60 hover:text-accent-300"
                  }`}
                >
                  <i className={favorited ? "ri-heart-3-fill text-sm" : "ri-heart-3-line text-sm"} aria-hidden="true" />
                  {favorited ? "已收藏" : "收藏"}
                </button>
                <button
                  type="button"
                  onClick={share}
                  className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border border-foreground-50/10 px-3 py-1.5 text-xs text-foreground-300 transition-colors hover:border-primary-400/60 hover:text-primary-300 md:text-sm"
                >
                  <i className="ri-link text-sm" aria-hidden="true" />
                  {copied ? "链接已复制" : "分享"}
                </button>
              </div>
            </div>

            <article className="mt-5">
              <h1 className="font-heading text-xl font-black leading-snug text-foreground-50 md:text-3xl">
                {drama.title}
                <span className="ml-2 font-body text-sm font-normal text-foreground-400 md:text-base">
                  第 {episode} 集
                </span>
              </h1>

              <p className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-foreground-500 md:text-xs">
                <span className="flex items-center gap-1 font-semibold text-primary-300">
                  <i className="ri-star-fill text-[10px]" aria-hidden="true" />
                  {drama.rating.toFixed(1)}
                </span>
                <span>{drama.year}</span>
                <span>{regionLabel(drama.region)}</span>
                <span>{drama.status}</span>
                <span>{drama.episodes}集</span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line text-sm" aria-hidden="true" />
                  {drama.views}
                </span>
                {drama.exclusive && (
                  <span className="rounded bg-accent-500 px-1.5 py-0.5 text-[10px] font-bold text-background-50">
                    独家
                  </span>
                )}
              </p>

              <ul className="mt-3 flex flex-wrap gap-1.5">
                {drama.tags.map((tag) => (
                  <li key={tag}>
                    <Link
                      to={`/category?q=${encodeURIComponent(tag)}`}
                      className="cursor-pointer whitespace-nowrap rounded-full border border-foreground-50/10 bg-foreground-50/[0.04] px-2.5 py-1 text-[11px] text-foreground-400 transition-colors hover:border-primary-400/60 hover:text-primary-300"
                    >
                      #{tag}
                    </Link>
                  </li>
                ))}
              </ul>

              <section aria-labelledby="synopsis-heading" className="mt-6">
                <h2 id="synopsis-heading" className="font-heading text-base font-bold text-foreground-100 md:text-lg">
                  剧情简介
                </h2>
                <p className="mt-2 text-xs leading-relaxed text-foreground-400 md:text-sm">{drama.synopsis}</p>
              </section>

              <section aria-labelledby="info-heading" className="mt-6">
                <h2 id="info-heading" className="font-heading text-base font-bold text-foreground-100 md:text-lg">
                  剧集信息
                </h2>
                <dl className="mt-3 grid grid-cols-1 gap-x-8 gap-y-2.5 rounded-xl border border-foreground-50/10 bg-foreground-50/[0.02] p-4 text-xs sm:grid-cols-2 md:text-sm">
                  <div className="flex gap-2">
                    <dt className="shrink-0 text-foreground-500">导演</dt>
                    <dd className="text-foreground-200">{drama.director}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="shrink-0 text-foreground-500">主演</dt>
                    <dd className="text-foreground-200">{drama.cast.join(" / ")}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="shrink-0 text-foreground-500">类型</dt>
                    <dd className="text-foreground-200">{categoryLabel(drama.category)}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="shrink-0 text-foreground-500">地区 / 年份</dt>
                    <dd className="text-foreground-200">
                      {regionLabel(drama.region)} · {drama.year}
                    </dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="shrink-0 text-foreground-500">更新状态</dt>
                    <dd className="text-foreground-200">
                      {drama.status}（共 {drama.episodes} 集，更新至 {drama.updatedEpisode} 集）
                    </dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="shrink-0 text-foreground-500">播放量</dt>
                    <dd className="text-foreground-200">{drama.views}</dd>
                  </div>
                </dl>
              </section>
            </article>
          </div>

          <aside aria-label="选集与相关信息" className="min-w-0">
            <div className="lg:sticky lg:top-20">
              <EpisodePanel
                total={drama.episodes}
                current={episode}
                freeCount={FREE_COUNT}
                onSelect={goEpisode}
              />

              <div className="mt-3 rounded-2xl border border-primary-400/20 bg-primary-500/[0.07] p-4">
                <h2 className="flex items-center gap-1.5 font-heading text-sm font-bold text-primary-200">
                  <i className="ri-vip-crown-2-line text-base" aria-hidden="true" />
                  追剧提示
                </h2>
                <p className="mt-2 text-[11px] leading-relaxed text-foreground-400">
                  本站剧集按集更新，建议收藏本剧以便第一时间收到更新提醒。播放历史会自动保存在本地，下次打开可继续观看。
                </p>
              </div>
            </div>
          </aside>
        </div>

        <PosterCarousel
          headingId="recommend-heading"
          title="猜你喜欢 · 同类型推荐"
          subtitle={`喜欢《${drama.title}》的观众还在看这些`}
          dramas={recommendations}
          moreHref={`/category?type=${drama.category}`}
        />
      </main>
      <SiteFooter />
    </>
  );
}